import { Button, Input, InputNumber, notification } from 'antd';
import { AddMoreBtnContainer, ImportantNoteOrder, ImportantNoteYear, StyledForm } from './style';
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useUpdateProfessionalClientImportantNotes } from '../../../hooks/professionals/useClientGeneralInfo';
import { useUpdateClientImportantNotes } from '../../../hooks/client/useClientGeneralInfo';
import { useGetProfessionalClient } from '../../../hooks/professionals/useProfessionalClient';
import { NotificationTitle } from '../../Common/Notification/style';
import { CheckCircleOutlined } from '@ant-design/icons';
import { theme } from '../../../styles/theme';

const { TextArea } = Input;

const ImportantNotesForm = () => {
  const [api, contextHolder] = notification.useNotification();
  const [form] = StyledForm.useForm();
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const { user } = useSelector((state: RootState) => state.auth);
  const isClient = !!user && 'professionalCode' in user;

  const { update: updateProfessional, loading: loadingProfessional } = useUpdateProfessionalClientImportantNotes();
  const { update: updateClient, loading: loadingClient } = useUpdateClientImportantNotes();
  const { refetch: refetchClient } = useGetProfessionalClient(selectedClient?.id as string);

  useEffect(() => {
    if (selectedClient?.importantNotesToProfessional) {
      const initialNotes = selectedClient.importantNotesToProfessional.map((n: any) => ({
        id: n.id,
        note: n.note,
        yearOfEvent: n.yearOfEvent ? parseInt(n.yearOfEvent, 10) : undefined,
      }));
      form.setFieldsValue({ importantNotes: initialNotes });
    } else {
      form.setFieldsValue({ importantNotes: [{ note: '', yearOfEvent: undefined }] });
    }
  }, [selectedClient, form]);

  const onFinish = async (values: any) => {
    const notes = values.importantNotes || [];
    const validationErrors: string[] = [];

    const paramsToSend = notes
      .map((n: any, index: number) => {
        if (!n || n._destroy) return null;

        if (!n.note) {
          validationErrors.push(`Note content is missing for entry ${index + 1}.`);
        }
        const parsedYear = parseInt(n.yearOfEvent, 10);
        if (!n.yearOfEvent || isNaN(parsedYear)) {
          validationErrors.push(`Year is missing or invalid for entry ${index + 1}.`);
        }

        const idValue = (n.id != null && n.id !== '' && String(n.id) !== '0') ? String(n.id) : '';

        return {
          id: idValue,
          note: n.note,
          yearOfEvent: isNaN(parsedYear) ? 0 : parsedYear,
        };
      })
      .filter((item: any) => item !== null);

    if (validationErrors.length > 0) {
      api.warning({
        message: <NotificationTitle>Missing Information</NotificationTitle>,
        description: (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {validationErrors.map((err, i) => (
              <div key={i} style={{ marginBottom: '4px' }}>• {err}</div>
            ))}
          </div>
        ),
        placement: 'topRight',
        style: { background: `${theme.bg.secondary}`, borderRadius: 10 },
      });
      return;
    }

    try {
      let res;
      if (isClient) {
        res = await updateClient(paramsToSend);
      } else {
        res = await updateProfessional(selectedClient?.id as string, paramsToSend);
      }

      const success = isClient ? res?.clientUpdateImportantNotes : res?.professionalClientUpdateImportantNotes;

      if (success) {
        await refetchClient();
        api.info({
          message: <NotificationTitle>Important notes updated successfully</NotificationTitle>,
          placement: 'topRight',
          icon: <CheckCircleOutlined style={{ color: theme.color.primary }} />,
          style: { background: `${theme.bg.secondary}`, borderRadius: 10, alignItems: 'center', display: 'flex' },
        });
      }
    } catch (error: any) {
      console.error('Error saving important notes:', error);

      let description = 'There was an issue saving the details. Please try again.';
      if (error.graphQLErrors?.length) {
        description = error.graphQLErrors.map((e: any) => e.message).join(', ');
      } else if (error.message) {
        description = error.message;
      }

      api.error({
        message: <NotificationTitle>Error saving important notes</NotificationTitle>,
        description: description,
        placement: 'topRight',
        style: { background: `${theme.bg.secondary}`, borderRadius: 10, alignItems: 'center', display: 'flex' },
      });
    }
  };

  const onRemoveNote = (fieldIndex: any) => {
    const oldNotes = form.getFieldValue('importantNotes') || [];
    const newNotes = oldNotes.map((item: any, index: number) =>
      index === fieldIndex ? { ...item, _destroy: true } : item
    );
    form.setFieldsValue({ importantNotes: newNotes });
  };

  return (
    <StyledForm
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="important_notes_form"
      onFinish={onFinish}
      initialValues={{}}
      style={{ marginTop: '1rem' }}
      scrollToFirstError
    >
      {contextHolder}
      <p>Important things about you to know during the duration of the consulting process.</p>

      <StyledForm.List name="importantNotes">
        {(fields, { add }) => (
          <>
            {fields.map((field, index) => {
              const noteData = form.getFieldValue(['importantNotes', field.name]);
              if (noteData?._destroy) return null;

              return (
                <div key={field.key} style={{ marginBottom: '2rem', borderBottom: index < fields.length - 1 ? `1px solid ${theme.bg.secondary}` : 'none', paddingBottom: '1rem' }}>
                  <ImportantNoteOrder isEmpty={false}>
                    <p>{`${index + 1})`}</p>
                    <Button
                      size="small"
                      onClick={() => onRemoveNote(field.name)}
                    >
                      Remove
                    </Button>
                  </ImportantNoteOrder>

                  <StyledForm.Item
                    name={[field.name, 'id']}
                    hidden
                  >
                    <Input />
                  </StyledForm.Item>

                  <StyledForm.Item
                    name={[field.name, 'note']}
                    rules={[{ required: true, message: 'Please enter a note' }]}
                  >
                    <TextArea rows={4} placeholder="Describe the important event or note..." />
                  </StyledForm.Item>

                  <StyledForm.Item
                    name={[field.name, 'yearOfEvent']}
                    rules={[{ required: true, message: 'Please specify the year' }]}
                  >
                    <ImportantNoteYear>
                      <p>Fill in the year of the event described above:</p>
                      <InputNumber placeholder="e.g. 2016" style={{ width: '100%' }} />
                    </ImportantNoteYear>
                  </StyledForm.Item>
                </div>
              );
            })}
            <AddMoreBtnContainer>
              <Button
                size="small"
                onClick={() => add()}
              >
                Add more
              </Button>
            </AddMoreBtnContainer>
          </>
        )}
      </StyledForm.List>

      <StyledForm.Item {..._TAIL_FORM_ITEM_LAYOUT}>
        <Button type="primary" htmlType="submit" loading={isClient ? loadingClient : loadingProfessional}>
          Save
        </Button>
      </StyledForm.Item>
    </StyledForm>
  )
};

export default ImportantNotesForm;